import type { PilotMeta } from "../simulation/pilot-meta";
import type { RunSummary } from "../simulation/snapshot";

export type AchievementContext = {
  totalKills: number;
  bestStreak: number;
  bestChain: number;
  simTime: number;
  salvageCollected: number;
  eliteCores: number;
  skillNodes: number;
  runsCompleted: number;
  aceKilled: boolean;
  incursionSurvived: boolean;
  roleCount: number;
  hadTripleKill: boolean;
  hadDoubleKill: boolean;
  tacticianCount: number;
  bossArenasCleared: number;
  longestNoHullDamageS: number;
};

export function evaluateAchievements(
  meta: PilotMeta,
  summary: RunSummary | null,
  ctx: AchievementContext,
): string[] {
  const unlocked: string[] = [];
  const have = new Set(meta.achievements);

  const tryUnlock = (id: string, condition: boolean): void => {
    if (condition && !have.has(id)) unlocked.push(id);
  };

  tryUnlock("first_blood", ctx.totalKills >= 1);
  tryUnlock("kill_10", ctx.totalKills >= 10);
  tryUnlock("kill_50", ctx.totalKills >= 50);
  tryUnlock("streak_10", ctx.bestStreak >= 10);
  tryUnlock("chain_15", ctx.bestChain >= 15);
  tryUnlock("salvage_100", ctx.salvageCollected >= 100);
  tryUnlock("survive_120", ctx.simTime >= 120);
  tryUnlock("survive_300", ctx.simTime >= 300);
  tryUnlock("elite_core", ctx.eliteCores > 0);
  tryUnlock("incursion_clear", ctx.incursionSurvived);
  tryUnlock("ace_slayer", ctx.aceKilled);
  tryUnlock("boss_arena", ctx.bossArenasCleared > 0);
  tryUnlock("triple_kill", ctx.hadTripleKill);
  tryUnlock("double_kill", ctx.hadDoubleKill);
  tryUnlock("no_damage_30", ctx.longestNoHullDamageS >= 30);
  tryUnlock("tactician_3", ctx.tacticianCount >= 3);
  tryUnlock("all_roles", ctx.roleCount >= 5);
  tryUnlock("rank_5", meta.rank >= 5);
  tryUnlock("rank_10", meta.rank >= 10);
  tryUnlock("rank_25", meta.rank >= 25);

  if (summary) {
    tryUnlock("grade_a", summary.grade === "A" || summary.grade === "S");
    tryUnlock("grade_s", summary.grade === "S");
    tryUnlock("score_5k", summary.score >= 5000);
    tryUnlock("score_15k", summary.score >= 15000);
    tryUnlock("level_10", summary.level >= 10);
    tryUnlock("level_20", summary.level >= 20);
    tryUnlock("skills_6", summary.takenNodes.length >= 6);
    if (summary.outcome === "extracted") {
      tryUnlock("extract_once", true);
      tryUnlock("extract_5", ctx.runsCompleted >= 5);
    }
  }

  return unlocked;
}
